import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import {
  alreadyInBuyerCrm,
  type PrequalificationBuyerCrmMatchDto,
  type PrequalificationBuyerCrmResultDto,
  type PrequalificationBuyerCrmStateDto,
  type PrequalificationLinkedBuyerDto,
  type SendPrequalificationToBuyerCrmPayload,
} from "@/api/prequalification";
import { useSendPrequalificationToBuyerCrm } from "@/hooks/prequalification/usePrequalification";
import { Badge, Button } from "@/design-system/components/ui";
import { formatPhone } from "@/design-system/lib/format";
import { Modal } from "@/components/ui/Modal";
import { outcomeLabel } from "./PrequalificationListPage";

/**
 * The link-back that replaces the send button once a completion is in the
 * Buyer CRM (ruling L2/Z1).
 *
 * 🔴 Z5: the server withholds the buyer's id and name from a caller without
 * Buyers.Read. That caller still learns the completion is in the Buyer CRM —
 * which is what stops them looking for the button — but sees no name and no
 * link, since the link would only lead to a 403.
 */
export function BuyerCrmLinkBack({ linked }: { linked: PrequalificationLinkedBuyerDto }) {
  const archived = linked.isArchived ? " (archived)" : "";
  if (!linked.buyerLeadId || !linked.name) {
    return (
      <span className="text-caption text-gray-mid" data-testid="prequal-buyer-crm-linkback">
        In Buyer CRM{archived}
      </span>
    );
  }
  return (
    <Link
      to={`/buyers/${linked.buyerLeadId}`}
      className="text-caption text-gold-shadow hover:underline inline-flex items-center gap-space-1"
      data-testid="prequal-buyer-crm-linkback"
    >
      In Buyer CRM: {linked.name}
      {archived} <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
    </Link>
  );
}

/** The one sentence a result reports. Each outcome names what actually
 *  happened — "recorded" in particular must not read as a link, because the
 *  buyer's packages keep using the prequalification they already had (Z2). */
function resultSentence(r: PrequalificationBuyerCrmResultDto): string {
  switch (r.outcome) {
    case "created":
      return `${r.buyerName} is in the assignment queue${r.buyerTeamName ? ` for ${r.buyerTeamName}` : ""}, unassigned.`;
    case "linked":
      return `Linked to ${r.buyerName}.`;
    case "recorded":
      return `Recorded against ${r.buyerName}. Their packages keep using prequalification ${r.existingPrequalificationReference ?? "already on file"}.`;
    default:
      return `This completion was already ${r.buyerName}'s prequalification.`;
  }
}

export function SendToBuyerCrmModal({
  submissionId,
  state,
  onClose,
}: {
  submissionId: string;
  state: PrequalificationBuyerCrmStateDto;
  onClose: () => void;
}) {
  const send = useSendPrequalificationToBuyerCrm(submissionId);
  const [error, setError] = useState<string | null>(null);
  /** The buyer a 409 named: somebody sent or linked this completion while the
   *  dialog was open. Rendered as a link-back in place of every action. */
  const [beaten, setBeaten] = useState<PrequalificationLinkedBuyerDto | null>(null);
  const [result, setResult] = useState<PrequalificationBuyerCrmResultDto | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const hasMatches = state.matches.length > 0;
  const blocked = !!state.contactProblem;

  const run = (body: SendPrequalificationToBuyerCrmPayload, key: string) => {
    setError(null);
    setPendingId(key);
    send.mutate(body, {
      onSuccess: (r) => setResult(r),
      onError: (e: Error) => {
        const linked = alreadyInBuyerCrm(e);
        if (linked) setBeaten(linked);
        else setError(e.message);
      },
      onSettled: () => setPendingId(null),
    });
  };

  const done = result !== null || beaten !== null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Send to the assignment queue"
      description="Creates an unassigned buyer in the Buyer CRM assignment queue from this completion, or links it to a buyer who is already there."
      footer={
        <div className="flex justify-end gap-space-2">
          {done ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
                Cancel
              </Button>
              {hasMatches ? (
                <Button
                  variant="secondary"
                  disabled={send.isPending || blocked}
                  onClick={() => run({ createAnyway: true }, "create")}
                >
                  {pendingId === "create" ? "Sending…" : "Create new anyway"}
                </Button>
              ) : (
                <Button disabled={send.isPending || blocked} onClick={() => run({}, "create")}>
                  {pendingId === "create" ? "Sending…" : "Send"}
                </Button>
              )}
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="prequal-buyer-crm-dialog">
        {result && (
          <div
            role="status"
            className="text-small text-status-success border border-status-success/40 rounded-md p-space-2"
            data-testid="prequal-buyer-crm-result"
          >
            {resultSentence(result)}{" "}
            <Link to={`/buyers/${result.buyerLeadId}`} className="text-gold-shadow hover:underline">
              Open buyer
            </Link>
          </div>
        )}
        {beaten && (
          <div
            role="alert"
            className="text-small text-status-warning border border-status-warning/40 rounded-md p-space-2"
            data-testid="prequal-buyer-crm-beaten"
          >
            Somebody else has already put this completion in the Buyer CRM.{" "}
            <BuyerCrmLinkBack linked={beaten} />
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="text-small text-status-error border border-status-error/40 rounded-md p-space-2"
          >
            {error}
          </div>
        )}

        <dl className="grid grid-cols-[auto_1fr] gap-x-space-3 gap-y-space-1 text-small">
          <dt className="text-gray-mid">Name</dt>
          <dd>{state.name || "—"}</dd>
          <dt className="text-gray-mid">Email</dt>
          <dd>{state.email || "—"}</dd>
          <dt className="text-gray-mid">Phone</dt>
          <dd>{state.phone ? formatPhone(state.phone) : "—"}</dd>
          <dt className="text-gray-mid">Outcome</dt>
          <dd>{outcomeLabel(state.outcome)}</dd>
          <dt className="text-gray-mid">Came from</dt>
          <dd>{state.cameFrom}</dd>
        </dl>

        {state.contactProblem && (
          <div
            role="alert"
            className="text-small text-status-error border border-status-error/40 rounded-md p-space-2"
            data-testid="prequal-buyer-crm-contact-problem"
          >
            {state.contactProblem}
          </div>
        )}
        {state.phoneNote && (
          <p className="text-caption text-gray-mid" data-testid="prequal-buyer-crm-phone-note">
            {state.phoneNote}
          </p>
        )}

        {hasMatches && !done && (
          <div className="flex flex-col gap-space-2">
            <p className="text-small text-onyx">
              {state.matches.length === 1
                ? "A buyer in the Buyer CRM shares this contact detail."
                : `${state.matches.length} buyers in the Buyer CRM share this contact detail.`}{" "}
              Link to one, or create a new buyer marked as a possible duplicate of the first.
            </p>
            <ul className="flex flex-col gap-space-2">
              {state.matches.map((m) => (
                <MatchRow
                  key={m.buyerLeadId}
                  match={m}
                  pending={pendingId === m.buyerLeadId}
                  disabled={send.isPending}
                  onLink={() => run({ linkToBuyerLeadId: m.buyerLeadId }, m.buyerLeadId)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

function MatchRow({
  match,
  pending,
  disabled,
  onLink,
}: {
  match: PrequalificationBuyerCrmMatchDto;
  pending: boolean;
  disabled: boolean;
  onLink: () => void;
}) {
  return (
    <li
      className="border border-border-light rounded-md p-space-2 flex flex-wrap items-start justify-between gap-space-2"
      data-testid={`prequal-buyer-crm-match-${match.buyerLeadId}`}
    >
      <div className="flex flex-col gap-space-1 min-w-0">
        <div className="flex flex-wrap items-center gap-space-2">
          <Link to={`/buyers/${match.buyerLeadId}`} className="text-small text-gold-shadow hover:underline">
            {match.name}
          </Link>
          <Badge status="draft">{match.status}</Badge>
          {match.isArchived && <Badge status="archived">Archived</Badge>}
        </div>
        <span className="text-caption text-gray-mid">
          Matched on {match.matchedOn}
          {match.email ? ` · ${match.email}` : ""}
          {match.phone ? ` · ${formatPhone(match.phone)}` : ""}
          {` · ${match.assignedSpecialistName ?? "Unassigned"}`}
        </span>
        {match.existingPrequalificationReference && (
          <span className="text-caption text-gray-mid" data-testid="prequal-buyer-crm-match-existing">
            Already has prequalification {match.existingPrequalificationReference}. Linking records this
            one; their packages keep using {match.existingPrequalificationReference}.
          </span>
        )}
        {!match.canLink && match.linkBlockedReason && (
          <span className="text-caption text-gray-mid">{match.linkBlockedReason}</span>
        )}
      </div>
      <Button size="sm" variant="secondary" disabled={disabled || !match.canLink} onClick={onLink}>
        {pending ? "Linking…" : "Link to this buyer"}
      </Button>
    </li>
  );
}
